import React, { useEffect, useRef } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useTranslation } from "react-i18next";
import { pickLang } from "../../../../Utils/catalogName";
import {
  catalogCardStyle,
  catalogPathStyle,
  catalogTitleStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
} from "./styles";

/**
 * Renders the answer of POST pricing/catalog/resolve (and of a later create)
 * inside MissingServiceModal. Presentational only: every action is a callback.
 *
 * view:
 *   { kind: "result", result }               — match / similar / create
 *   { kind: "created", created, proposal }   — 200 from catalog/create
 *   { kind: "duplicate", match }             — 409 from catalog/create
 */
const MatchCard = ({ match, language, t, onOpenPricelist, busy }) => {
  const name = pickLang(match.name, language);
  const path = pickLang(match.path, language);
  return (
    <Box sx={catalogCardStyle} data-testid="catalog-match">
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={catalogTitleStyle}>{name}</Typography>
        <Typography sx={catalogPathStyle}>{path}</Typography>
      </Box>
      <Button
        variant="contained"
        sx={primaryButtonStyle}
        disabled={busy}
        onClick={() =>
          onOpenPricelist(match.pricelist_id, match.supplier_has_pricelist)
        }
      >
        {match.supplier_has_pricelist
          ? t("catalog.open_pricelist")
          : t("catalog.join_pricelist")}
      </Button>
    </Box>
  );
};

const ProposalDetails = ({ proposal, price, language, t }) => {
  const variantName = pickLang(proposal.variant?.name, language);
  const serviceName = pickLang(proposal.service_name, language);
  const path = [pickLang(proposal.path, language), serviceName]
    .filter(Boolean)
    .join(" › ");
  const rows = [
    [t("catalog.field_name"), variantName],
    [t("catalog.field_path"), path],
    [
      t("catalog.field_unit"),
      proposal.unit?.name ? t(proposal.unit.name, { ns: "units" }) : "",
    ],
    [t("catalog.field_price"), price ? String(price) : ""],
  ].filter(([, value]) => value);
  return (
    <Box component="dl" sx={{ m: 0, display: "grid", gap: "6px" }}>
      {rows.map(([label, value]) => (
        <Box key={label} sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Typography component="dt" sx={catalogPathStyle}>
            {label}:
          </Typography>
          <Typography component="dd" sx={{ m: 0, fontSize: "14px" }}>
            {value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const CatalogResolveResult = ({
  view,
  requestText,
  price,
  adding = false,
  busy = false,
  createError = false,
  onOpenPricelist,
  onAdd,
}) => {
  const { t, i18n } = useTranslation(["missing-service-modal", "units"]);
  const language = i18n?.language;
  const headingRef = useRef(null);

  // Move focus to the new content so keyboard and screen-reader users land on
  // the answer instead of on a button that no longer exists.
  useEffect(() => {
    headingRef.current?.focus();
  }, [view]);

  if (!view) return null;
  const isBusy = busy || adding;

  const heading = (text) => (
    <Typography
      ref={headingRef}
      tabIndex={-1}
      component="h3"
      sx={{ fontSize: "16px", fontWeight: 600, outline: "none" }}
    >
      {text}
    </Typography>
  );

  const matchList = (matches) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {matches.map((match) => (
        <MatchCard
          key={`${match.service_id}-${match.variant_id ?? ""}`}
          {...{ match, language, t, onOpenPricelist, busy: isBusy }}
        />
      ))}
    </Box>
  );

  const addButton = (variant, label) => (
    <Button
      variant={variant}
      sx={variant === "contained" ? primaryButtonStyle : secondaryButtonStyle}
      disabled={isBusy}
      onClick={onAdd}
      startIcon={adding ? <CircularProgress size={16} color="inherit" /> : null}
    >
      {adding ? t("catalog.adding") : label}
    </Button>
  );

  const errorLine = createError ? (
    <Typography role="alert" sx={{ color: "#D32F2F", fontSize: "14px" }}>
      {createError === "rate_limited"
        ? t("rate_limited")
        : t("catalog.create_error")}
    </Typography>
  ) : null;

  const recheckedLine = view.rechecked ? (
    <Typography sx={catalogPathStyle}>{t("catalog.rechecked")}</Typography>
  ) : null;

  let body = null;

  if (view.kind === "unsupported") {
    const key = ["not_a_service", "not_allowed"].includes(view.decision)
      ? view.decision
      : "out_of_scope";
    body = (
      <>
        {heading(t(`catalog.${key}_title`))}
        <Typography sx={{ fontSize: "14px" }}>
          {t(`catalog.${key}_text`)}
        </Typography>
      </>
    );
  } else if (view.kind === "created") {
    const { created, proposal } = view;
    const name = pickLang(proposal?.variant?.name, language) || requestText;
    const path = pickLang(proposal?.path, language);
    body = (
      <>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CheckCircleOutlineIcon sx={{ color: "#248B70" }} />
          {heading(t("catalog.created_title"))}
        </Box>
        <Typography sx={{ fontSize: "14px" }}>
          {t("catalog.created_text", { name, path })}{" "}
          {created.supplier_has_pricelist
            ? t("catalog.created_price_later")
            : t("catalog.created_join_hint")}
        </Typography>
        <Box>
          <Button
            variant="contained"
            sx={primaryButtonStyle}
            disabled={isBusy}
            onClick={() =>
              onOpenPricelist(
                created.pricelist_id,
                created.supplier_has_pricelist
              )
            }
          >
            {created.supplier_has_pricelist
              ? t("catalog.open_pricelist")
              : t("catalog.join_pricelist")}
          </Button>
        </Box>
      </>
    );
  } else if (view.kind === "duplicate") {
    body = (
      <>
        {heading(t("catalog.duplicate_title"))}
        <Typography sx={{ fontSize: "14px" }}>
          {t("catalog.duplicate_text")}
        </Typography>
        {view.match ? matchList([view.match]) : null}
      </>
    );
  } else {
    const { decision, matches = [], proposal } = view.result || {};
    if (decision === "match") {
      body = (
        <>
          {heading(t("catalog.match_title"))}
          <Typography sx={{ fontSize: "14px" }}>
            {t("catalog.match_text")}
          </Typography>
          {matchList(matches)}
        </>
      );
    } else if (decision === "similar") {
      body = (
        <>
          {heading(t("catalog.similar_title"))}
          <Typography sx={{ fontSize: "14px" }}>
            {t("catalog.similar_text")}
          </Typography>
          {matchList(matches)}
          {proposal ? (
            <Box sx={catalogCardStyle} data-testid="catalog-proposal">
              <Typography sx={{ fontSize: "14px", minWidth: 0 }}>
                {t("catalog.none_of_these", {
                  name:
                    pickLang(proposal.variant?.name, language) || requestText,
                  path: pickLang(proposal.path, language),
                })}
              </Typography>
              {addButton("outlined", t("catalog.add_as_new"))}
            </Box>
          ) : null}
          {errorLine}
        </>
      );
    } else if (decision === "create" && proposal) {
      body = (
        <>
          {heading(t("catalog.create_title"))}
          <Typography sx={{ fontSize: "14px" }}>
            {t("catalog.create_text", { name: requestText })}
          </Typography>
          <Box
            sx={{
              ...catalogCardStyle,
              flexDirection: "column",
              alignItems: "stretch",
            }}
            data-testid="catalog-proposal"
          >
            <ProposalDetails {...{ proposal, price, language, t }} />
            {price ? (
              <Typography sx={catalogPathStyle}>
                {t("catalog.price_hint")}
              </Typography>
            ) : null}
            <Box>{addButton("contained", t("catalog.add_to_catalog"))}</Box>
          </Box>
          {errorLine}
        </>
      );
    }
  }

  if (!body) return null;

  return (
    <Box
      role="region"
      aria-label={t("catalog.result_region")}
      aria-live="polite"
      sx={{ display: "flex", flexDirection: "column", gap: "12px" }}
    >
      {recheckedLine}
      {body}
    </Box>
  );
};

export default CatalogResolveResult;
